import { formatDuration } from 'date-fns';

import { AppSubFeatures } from 'hooks/useHasFeature';
import {
    socialChannels,
    taskChannels,
    voiceChannels,
    webchatChannels,
} from 'store/contactHistory/contactHistory.constants';
import { ITag, TagColourMap, ChannelFormatMap } from './types';
import { IInteraction, ChannelType } from '../types';
import { IEmailAttachment } from 'components/EmailMessage/types';

import FacebookIcon from 'images/icon-facebook.svg';
import TwitterIcon from 'images/icon-twitter.svg';
import InstagramIcon from 'images/icon-instagram.svg';
import WhatsappIcon from 'images/icon-whatsapp.svg';
import SMSIcon from 'images/icon-sms.svg';
import VoiceInboundIcon from 'images/icon-voice-inbound.svg';
import VoiceOutboundIcon from 'images/icon-voice-outbound.svg';
import EyeOutlineIcon from 'images/icon-eye-outline.svg';
import WebchatIcon from 'images/icon-webchat.svg';
import EmailIcon from 'images/icon-email-outline.svg';
import EmailInboundIcon from 'images/icon-email-inbound.svg';
import EmailOutboundIcon from 'images/icon-email-outbound.svg';
import { ICallContactAttributes } from 'store/metrics/metrics.state';

const tagColourMap: TagColourMap = {
    'TWITTER DM': 'blue',
    'TWITTER TWEET': 'blue',
    'FACEBOOK MESSENGER': 'blue',
    'FACEBOOK POST': 'blue',
    'INSTAGRAM IG-DM': 'blue',
    'INSTAGRAM IG-COMMENT': 'blue',
    'WHATSAPP WHATSAPP-DM': 'blue',
    'SMS SMS-DM': 'blue',
    'SMS OUTBOUND': 'purple',
    'SMS CAMPAIGN-MESSAGE': 'purple',
    'SMS MARKETING-MESSAGE': 'purple',
    'VOICE INBOUND': 'blue',
    'VOICE OUTBOUND': 'purple',
    'VOICE MONITOR': 'orange',
    'VOICE TRANSFER': 'pink',
    WEBCHAT: 'blue',
    'TASK INBOUND': 'blue',
    'TASK OUTBOUND': 'purple',
    'TASK CCREPLY': 'blue',
    'sa-reason': 'sa-blue',
    UNKNOWN: 'dark',
};
export const possibleEmailAttributes = ['sa-customer-email', 'email', 'customerEmail', 'sa-customerEmail', 'sa-email', 'sa-customer-endpoint-address'];

export const channelFormatMapping: ChannelFormatMap = {
    'FACEBOOK MESSENGER': 'Facebook DM',
    'FACEBOOK POST': 'Facebook Comment',
    'INSTAGRAM IG-COMMENT': 'Insta-Comment',
    'INSTAGRAM IG-DM': 'Instagram DM',
    'SMS OUTBOUND': 'Outbound SMS',
    'SMS SMS-DM': 'SMS',
    'SMS CAMPAIGN-MESSAGE': 'SMS Campaign Message',
    'SMS MARKETING-MESSAGE': 'SMS Marketing Message',
    TASK: 'Email Channel',
    'TASK INBOUND': 'Inbound Email',
    'TASK OUTBOUND': 'Outbound Email',
    'TASK CCREPLY': 'Reply from Cc',
    'TWITTER DM': 'Twitter DM',
    'TWITTER TWEET': 'Twitter Tweet',
    'VOICE DISCONNECT': 'Voice Disconnect',
    'VOICE INBOUND': 'Inbound Call',
    'VOICE MONITOR': 'Monitored Call',
    'VOICE OUTBOUND': 'Outbound Call',
    'VOICE TRANSFER': 'Transferred Call',
    WEBCHAT: 'Webchat',
    'WHATSAPP WHATSAPP-DM': 'WhatsApp',
    UNKNOWN: 'Unknown Channel',
};

export const constructTags = (interaction: IInteraction): ITag[] => {
    const tags: ITag[] = [];

    const { channelType } = interaction;
    const formattedChannelType = channelFormatMapping[channelType];

    const { 'sa-reason': reasonForContact } = interaction.attributes || {};

    formattedChannelType &&
        tags.push(
            getCustomTagProperties(interaction) || {
                text: formattedChannelType,
                colour: tagColourMap?.[channelType] || tagColourMap.UNKNOWN,
            }
        );
    reasonForContact && tags.push({ text: reasonForContact, colour: tagColourMap['sa-reason'] });

    return tags;
};

export const getCustomTagProperties = (interaction: IInteraction): ITag | undefined => {
    const { channelType } = interaction;
    const relatedMessage = interaction.emailMessages?.find(msg => msg.messageId === interaction.messageId);

    if (channelType === 'TASK OUTBOUND' && relatedMessage?.forwardedTo) {
        return { text: 'Forwarded email', colour: 'sa-grey' };
    }

    if (channelType === 'TASK INBOUND' && relatedMessage?.externalFrom) {
        return { text: 'Reply to forward', colour: 'sa-grey' };
    }
};

export const constructDisplayName = (
    channelType: ChannelType,
    customerEndpointAddress?: string | undefined,
    attributes?: ICallContactAttributes,
    selectedMessageEndpointAddress?: string | undefined
): string | undefined => {
    let displayName: string | undefined;

    if (voiceChannels.includes(channelType)) {
        displayName = customerEndpointAddress;
    } else if (socialChannels.includes(channelType)) {
        const socialHandle = attributes?.['sa-customer-endpoint-address'];
        displayName = ['FACEBOOK', 'WHATSAPP', 'SMS'].some(channel => channelType.includes(channel))
            ? socialHandle
            : `@${socialHandle}`;
    } else if (webchatChannels.includes(channelType) || taskChannels.includes(channelType)) {
        if (channelType === 'TASK CCREPLY' && selectedMessageEndpointAddress) {
            return selectedMessageEndpointAddress
        }

        displayName = getEmailFromContactAttributes(attributes);
    }

    return displayName;
};

export const formatInteractionDuration = (duration: number): string => {
    let interactionDuration = 'Less than a second';

    const durationInSeconds = duration / 1000;
    const durationInMinutes = durationInSeconds / 60;
    const durationInHours = durationInMinutes / 60;
    const durationInDays = durationInHours / 24;

    if (durationInDays >= 1) {
        interactionDuration = 'Over a day';
    } else if (durationInSeconds >= 1) {
        const displaySeconds = Math.floor(durationInSeconds % 60);
        const displayMinutes = Math.floor(durationInMinutes % 60);
        const displayHours = Math.floor(durationInHours % 24);

        const durationObject: Duration = {
            hours: displayHours,
            minutes: displayMinutes,
            seconds: displaySeconds,
        };

        interactionDuration = formatDuration(durationObject);
    }
    return interactionDuration;
};

export const getChannelSubFeatures = (channelType: ChannelType): AppSubFeatures[] => {
    const {
        CONTACT_HISTORY_SOCIAL,
        CONTACT_HISTORY_SOCIAL_TWITTER_TWEET,
        CONTACT_HISTORY_SOCIAL_TWITTER_DM,
        CONTACT_HISTORY_SOCIAL_FACEBOOK_DM,
        CONTACT_HISTORY_SOCIAL_FACEBOOK_POST,
        CONTACT_HISTORY_SOCIAL_INSTAGRAM_DM,
        CONTACT_HISTORY_SOCIAL_INSTAGRAM_COMMENT,
        CONTACT_HISTORY_SOCIAL_SMS,
        CONTACT_HISTORY_MESSAGING_WHATSAPP_DM,
        CONTACT_HISTORY_VOICE,
        CONTACT_HISTORY_WEBCHAT,
        CONTACT_HISTORY_TASK,
    } = AppSubFeatures;

    const permissions: AppSubFeatures[] = [];

    switch (channelType) {
        case 'TWITTER TWEET':
            permissions.push(CONTACT_HISTORY_SOCIAL_TWITTER_TWEET, CONTACT_HISTORY_SOCIAL);
            break;
        case 'TWITTER DM':
            permissions.push(CONTACT_HISTORY_SOCIAL_TWITTER_DM, CONTACT_HISTORY_SOCIAL);
            break;
        case 'FACEBOOK MESSENGER':
            permissions.push(CONTACT_HISTORY_SOCIAL_FACEBOOK_DM, CONTACT_HISTORY_SOCIAL);
            break;
        case 'FACEBOOK POST':
            permissions.push(CONTACT_HISTORY_SOCIAL_FACEBOOK_POST, CONTACT_HISTORY_SOCIAL);
            break;
        case 'INSTAGRAM IG-DM':
            permissions.push(CONTACT_HISTORY_SOCIAL_INSTAGRAM_DM, CONTACT_HISTORY_SOCIAL);
            break;
        case 'INSTAGRAM IG-COMMENT':
            permissions.push(CONTACT_HISTORY_SOCIAL_INSTAGRAM_COMMENT, CONTACT_HISTORY_SOCIAL);
            break;
        case 'WHATSAPP WHATSAPP-DM':
            permissions.push(CONTACT_HISTORY_MESSAGING_WHATSAPP_DM, CONTACT_HISTORY_SOCIAL);
            break;
        case 'SMS SMS-DM':
            permissions.push(CONTACT_HISTORY_SOCIAL_SMS, CONTACT_HISTORY_SOCIAL);
            break;
        case 'VOICE INBOUND':
            permissions.push(CONTACT_HISTORY_VOICE);
            break;
        case 'VOICE OUTBOUND':
            permissions.push(CONTACT_HISTORY_VOICE);
            break;
        case 'VOICE TRANSFER':
            permissions.push(CONTACT_HISTORY_VOICE);
            break;
        case 'VOICE MONITOR':
            permissions.push(CONTACT_HISTORY_VOICE);
            break;
        case 'WEBCHAT':
            permissions.push(CONTACT_HISTORY_WEBCHAT);
            break;
        case 'TASK':
            permissions.push(CONTACT_HISTORY_TASK);
            break;
    }
    return permissions;
};

export const getEmailFromContactAttributes = (attributes?: ICallContactAttributes): string | undefined => {
    if (!attributes) return;

    // Search through the contact's attributes and grab the first of the email attributes list above if found
    const emailAttribute = Object.keys(attributes).find(attribute => possibleEmailAttributes.includes(attribute));
    return emailAttribute && attributes[emailAttribute];
};

export const getInteractionId = (interaction: IInteraction): string => {
    const { messageId, ID } = interaction;
    return messageId || ID;
};

export const attachmentsRequireTransformation = (attachments: IEmailAttachment[]): boolean => {
    return attachments.length > 0 && !attachments[0]?.hasOwnProperty('s3URL');
};

export const formatDisconnectReason = (reason: string): string => {
    switch (reason) {
        case 'CONTACT_FLOW_DISCONNECT':
            return 'SYSTEM DISCONNECT';
        default:
            return reason;
    }
};

export const getChannelIcon = (channelType: ChannelType): string | undefined => {
    let channelIcon;

    if (channelType.includes('TWITTER')) {
        channelIcon = TwitterIcon;
    } else if (channelType.includes('FACEBOOK')) {
        channelIcon = FacebookIcon;
    } else if (channelType.includes('INSTAGRAM')) {
        channelIcon = InstagramIcon;
    } else if (channelType.includes('WHATSAPP')) {
        channelIcon = WhatsappIcon;
    } else if (channelType.includes('SMS')) {
        channelIcon = SMSIcon;
    } else if (channelType === 'VOICE OUTBOUND' || channelType === 'VOICE TRANSFER') {
        channelIcon = VoiceOutboundIcon;
    } else if (channelType === 'VOICE INBOUND' || channelType === 'VOICE DISCONNECT') {
        channelIcon = VoiceInboundIcon;
    } else if (channelType === 'VOICE MONITOR') {
        channelIcon = EyeOutlineIcon;
    } else if (channelType === 'WEBCHAT') {
        channelIcon = WebchatIcon;
    } else if (channelType === 'TASK INBOUND' || channelType === 'TASK CCREPLY') {
        channelIcon = EmailInboundIcon;
    } else if (channelType === 'TASK OUTBOUND') {
        channelIcon = EmailOutboundIcon;
    } else if (channelType === 'TASK') {
        channelIcon = EmailIcon;
    }

    return channelIcon;
};

export const contactHasNoHistory = (interactions: IInteraction[] | undefined, loaded: boolean | undefined): boolean => {
    return !!((!interactions || interactions.length === 0) && loaded);
};
